<template>
  <div class="product" :style="productStyle" :class="{ boxed: backgroundImage }">
    <div class="product-image max-w-sm max-h-[22.8152rem] justify-self-center">
      <img class="w-full h-full object-contain" v-lazy="productImage" alt="Dr Hauschka" />
    </div>
    <div class="product-text w-full pl-20">
      <h3 class="product-title text-black text-base font-semibold">{{ title }}</h3>
      <ul class="product-description text-sm list-disc mt-4 pl-4">
        <li v-for="(description, index) in productDescription" :key="index">
          <span class="relative text-base">{{ description }}</span>
        </li>
      </ul>
      <div class="product-meta mt-4 font-semibold">
        {{ metaInfo }}
        <span class="font-semibold">{{ metaNumber }}</span>
      </div>
      <feature name="disable-cart">
        <div v-if="available" class="product-price text-base font-semibold mt-10">{{ priceString }}</div>
        <button v-if="available" class="btn-add-to-cart mt-10" @click="addToCart">
          <span>{{ textSnippets.add_to_basket }}</span>
        </button>
        <button v-else class="btn-add-to-cart mt-10 disabled" disabled="true">
          <span>{{ textSnippets.product_unavailable }}</span>
        </button>
      </feature>
    </div>
  </div>
</template>
<script lang="ts">
import { FeatureToggleComponent as feature } from 'vue-feature-toggle'
import { defineComponent, computed, inject } from 'vue'
import { useMainStore } from '@/store/main'
import utils from '@/utils'

export default defineComponent({
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    productImage: {
      type: String,
      default: '',
    },
    backgroundImage: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: [String, Array],
      default: '',
    },
    metaInfo: {
      type: String,
      default: '',
    },
    metaNumber: {
      type: String,
      default: '',
    },
    price: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
      default: 'EUR',
    },
    available: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    feature,
  },

  setup(props) {
    const { description, backgroundImage, price, currency } = props

    /**
     * register Store
     */
    const store = useMainStore()

    /**
     * inject local config
     */
    const config: any = inject('config')

    /**
     * @returns {Language} textSnippets
     */
    const textSnippets = computed(() => store.snippets)

    const productDescription = computed(() => {
      const isString = typeof description === 'string' || description instanceof String

      return isString ? description.split(',') : description
    })

    const productStyle = computed(() => {
      return {
        background: backgroundImage || 'transparent',
      }
    })
    const priceString = computed(() => {
      return utils.formatterCurrency(textSnippets.value.locale, currency).format(price)
    })

    /**
     * @param {IProduct} Product
     * @description add product to cart
     */
    const addToCart = () => {
      const product = {
        id: props.id,
        title: props.title,
        price: props.price,
        productImage: props.productImage,
        currency: props.currency,
        qty: 1,
      }
      store.addToCart(product)
      store.openCart()
      store.updateBackendCart(config)
    }
    return {
      productDescription,
      productStyle,
      addToCart,
      textSnippets,
      priceString,
    }
  },
})
</script>
<style scoped>
.product {
  @apply grid md:grid-cols-2 gap-10 md:gap-20 items-center;
}

.product-image {
  @apply w-full flex items-center justify-center md:p-0;
}

.product-text {
  @apply w-full flex items-start flex-col;
}

/* TODO: move this to Button Component */
.btn-add-to-cart {
  @apply md:w-auto uppercase py-1 px-10 text-black font-semibold border border-black hover:bg-black hover:text-white transition duration-300 ease-in-out;
}

.btn-add-to-cart.disabled {
  @apply text-dark-grey border-dark-grey hover:text-dark-grey hover:bg-transparent cursor-not-allowed;
}
</style>
